import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthUser } from "../../Context/AuthUserContext";
import { getCamiCodesListWithPaginationByGameId, getGamesList } from "../../Repository/Api";
import { buildWatchUrl, findLatestVideoFromCamiRow } from "../../Repository/CamiCodeVideosApi";

const PAGE_NUMBER = 1;
const PAGE_SIZE = 10;

function getDateRangeLabel(rows) {
  const values = (rows || [])
    .map((row) => (row?.date != null ? String(row.date).trim() : ""))
    .filter(Boolean);

  if (values.length === 0) {
    return null;
  }

  const uniqueValues = [...new Set(values)];
  if (uniqueValues.length === 1) {
    return uniqueValues[0];
  }

  const datedValues = uniqueValues
    .map((value) => ({ value, time: Date.parse(value) }))
    .filter((entry) => !Number.isNaN(entry.time))
    .sort((a, b) => a.time - b.time);

  if (datedValues.length >= 2) {
    return `${datedValues[0].value} - ${datedValues[datedValues.length - 1].value}`;
  }

  uniqueValues.sort();
  return `${uniqueValues[0]} - ${uniqueValues[uniqueValues.length - 1]}`;
}

function normalizeCamiListRows(data) {
  const raw = data?.list ?? data?.items ?? data?.data;
  const arr = Array.isArray(raw) ? raw : Array.isArray(data) ? data : [];
  return arr.map((item, idx) => ({
    id: item.id ?? item.camiCodeId ?? `cami-${idx}`,
    label:
      [item.vName, item.subject, item.title, item.gameName, item.camiCode].find((s) => s && String(s).trim()) ||
      item.camiCode ||
      "",
    videoId: String(item.videoId ?? item.videoID ?? "").trim(),
    latestVideoId: findLatestVideoFromCamiRow(item) || String(item.videoId ?? item.videoID ?? "").trim(),
    camiCode: item.camiCode ?? "",
    uniqueId: item.uniqueId ?? "",
    gameName: item.gameName ?? null,
    date: item.date ?? null,
    vName: item.vName ?? "",
    vEmail: item.vEmail ?? "",
  }));
}

export default function GamesAndVideos() {
  const theme = useTheme();
  const navy = theme.palette.primary.main;
  const navigate = useNavigate();
  const { profileReady, isLoggedIn, profile, refreshProfile } = useAuthUser();
  const [gamesCatalog, setGamesCatalog] = useState([]);
  const [listByGameId, setListByGameId] = useState({});
  const [expanded, setExpanded] = useState({});

  const filterId = profile?.id != null ? Number(profile.id) : 0;

  useEffect(() => {
    getGamesList()
      .then((list) => setGamesCatalog(Array.isArray(list) ? list : []))
      .catch(() => setGamesCatalog([]));
  }, []);

  /** Ensure profile includes backend `id` for FilterId (cached profile may predate this field). */
  useEffect(() => {
    if (!isLoggedIn || !profileReady || !profile) return;
    if (profile.id == null) {
      refreshProfile().catch(() => {});
    }
  }, [isLoggedIn, profileReady, profile, refreshProfile]);

  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      gamesCatalog.forEach((g) => {
        const key = String(g.id);
        if (!(key in next)) next[key] = false;
      });
      return next;
    });
  }, [gamesCatalog]);

  if (!profileReady) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  const toggleSection = (gameIdKey) => {
    setExpanded((prev) => {
      const willOpen = !prev[gameIdKey];
      if (willOpen) {
        const gameIdNum = Number(gameIdKey);
        setListByGameId((s) => ({
          ...s,
          [gameIdKey]: { ...(s[gameIdKey] || {}), loading: true, error: null },
        }));
        getCamiCodesListWithPaginationByGameId(gameIdNum, PAGE_NUMBER, PAGE_SIZE, filterId, "")
          .then((data) => {
            setListByGameId((s) => ({
              ...s,
              [gameIdKey]: {
                loading: false,
                error: null,
                rows: normalizeCamiListRows(data),
                totalItems: data?.totalItems,
              },
            }));
          })
          .catch((err) => {
            setListByGameId((s) => ({
              ...s,
              [gameIdKey]: {
                loading: false,
                error: err?.message || "Could not load list",
                rows: [],
              },
            }));
          });
      }
      return { ...prev, [gameIdKey]: willOpen };
    });
  };

  const goWatch = (row) => {
    const c = row.camiCode;
    const p = row.uniqueId;
    if (!c || !p) return;
    const redir = encodeURIComponent("/games-videos");
    navigate(
      buildWatchUrl({
        camiCode: c,
        uniqueId: p,
        videoId: row.latestVideoId || row.videoId || undefined,
        redir,
        email: row.vEmail || undefined,
        playConfirmed: true,
      })
    );
  };

  return (
    <Box sx={{ bgcolor: theme.palette.grey[50], minHeight: "calc(100vh - 220px)", py: { xs: 2, sm: 2.5 }, pb: { xs: 4, sm: 5 } }}>
      <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 2, sm: 2.5 } }}>
        <Typography
          component="h1"
          sx={{ fontWeight: 800, color: navy, fontSize: { xs: "1.35rem", sm: "1.5rem" }, mb: 2 }}
        >
          Games & Videos
        </Typography>

        {gamesCatalog.length === 0 ? (
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.95rem" }}>No games available.</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {gamesCatalog.map((game) => {
              const gameIdKey = String(game.id);
              const title = game.name ?? `Game ${game.id}`;
              const sectionState = listByGameId[gameIdKey] || {};
              const loading = sectionState.loading && expanded[gameIdKey];
              const err = sectionState.error;
              const rows = sectionState.rows;
              const loaded = rows !== undefined;
              const videoCount =
                typeof sectionState.totalItems === "number"
                  ? `${sectionState.totalItems} video(s)`
                  : null;
              const dateRangeLabel = loaded ? getDateRangeLabel(rows) : null;
              const metaParts = [`Game ID: ${gameIdKey}`];
              if (videoCount) metaParts.push(videoCount);
              if (dateRangeLabel) metaParts.push(dateRangeLabel);
              const countLine = metaParts.join(" \u00b7 ");

              return (
                <Card
                  key={gameIdKey}
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    backgroundColor: "#fff",
                  }}
                >
                  <CardContent
                    sx={{ px: 2.2, py: 1.6, "&:last-child": { pb: expanded[gameIdKey] ? 1.2 : 1.6 } }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: theme.palette.text.primary }}>
                          {title}
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem", color: theme.palette.text.secondary, mt: 0.25 }}>
                          {countLine}
                        </Typography>
                      </Box>
                      <IconButton
                        aria-expanded={expanded[gameIdKey]}
                        aria-label={expanded[gameIdKey] ? "Collapse" : "Expand"}
                        onClick={() => toggleSection(gameIdKey)}
                        sx={{ color: navy }}
                        size="small"
                      >
                        {expanded[gameIdKey] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </Box>

                    <Collapse in={expanded[gameIdKey]}>
                      <Box sx={{ mt: 1.2, pt: 0.5 }}>
                        {loading ? (
                          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : err && loaded ? (
                          <Typography sx={{ py: 2, color: "error.main", fontSize: "0.9rem" }}>{err}</Typography>
                        ) : loaded && (!rows || rows.length === 0) ? (
                          <Typography sx={{ py: 2, color: theme.palette.text.secondary, fontSize: "0.95rem" }}>
                            No videos for this game yet.
                          </Typography>
                        ) : (
                          (rows || []).map((row) => {
                            const detailLine = [row.camiCode, row.videoId].filter(Boolean).join(" · ");
                            const canPlay = !!(row.camiCode && row.uniqueId);
                            return (
                            <Box
                              key={row.id}
                              role="button"
                              tabIndex={canPlay ? 0 : -1}
                              onClick={canPlay ? () => goWatch(row) : undefined}
                              onKeyDown={
                                canPlay
                                  ? (event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        goWatch(row);
                                      }
                                    }
                                  : undefined
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                py: 1.2,
                                borderTop: `1px solid ${theme.palette.grey[200]}`,
                                cursor: canPlay ? "pointer" : "default",
                                opacity: canPlay ? 1 : 0.5,
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0, flex: 1 }}>
                                <PlayCircleOutlineIcon
                                  sx={{ color: navy, fontSize: "1.75rem", flexShrink: 0 }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    sx={{ fontWeight: 600, fontSize: "0.98rem", color: theme.palette.text.primary }}
                                  >
                                    {row.label || row.camiCode || "—"}
                                  </Typography>
                                  {detailLine ? (
                                    <Typography
                                      sx={{
                                        fontSize: "0.82rem",
                                        color: theme.palette.text.secondary,
                                        mt: 0.2,
                                        wordBreak: "break-all",
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      {detailLine}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                              <Typography
                                component="span"
                                sx={{
                                  color: navy,
                                  fontWeight: 600,
                                  fontSize: "0.95rem",
                                  flexShrink: 0,
                                  alignSelf: "center",
                                  lineHeight: 1.2,
                                  textDecoration: "underline",
                                }}
                              >
                                Play
                              </Typography>
                            </Box>
                          );
                          })
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
